<script setup>
import { computed, ref } from 'vue';
import { blockTouchesSprint } from '@/lib/schema';
import { teamLoad } from '@/lib/capacity';
import { hasDates, formatDay, sprintStartDate, currentSprint } from '@/lib/dates';
import { phaseVars } from '@/lib/palette';

const props = defineProps({
  roadmap: { type: Object, required: true }
});

const tip = ref(null);

const sprints = computed(() => Array.from({ length: props.roadmap.settings.sprintCount }, (_, i) => i + 1));
const dated = computed(() => hasDates(props.roadmap));
const thisSprint = computed(() => currentSprint(props.roadmap));

function sprintLabel(n) {
  return dated.value ? `S${n}, ${formatDay(sprintStartDate(props.roadmap.settings, n))}` : `Sprint ${n}`;
}

function projectsWhere(sprint, predicate) {
  return props.roadmap.projects.filter((p) => p.blocks.some((b) => blockTouchesSprint(b, sprint) && predicate(p, b)));
}

const phaseRows = computed(() =>
  props.roadmap.phases.map((phase) => ({
    key: phase.id,
    label: phase.name,
    style: phaseVars(phase.color),
    cells: sprints.value.map((n) => projectsWhere(n, (_, b) => b.phaseId === phase.id))
  }))
);

const people = computed(() => teamLoad(props.roadmap));

const overloaded = computed(() => people.value.filter((row) => row.cells.some((c) => c.over)).map((row) => row.member.name));

const unassigned = computed(() => props.roadmap.projects.filter((p) => !p.memberIds.length && p.blocks.length));

function level(count) {
  if (!count) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  return 3;
}

function cellText(cell) {
  if (cell.away) return 'Away';
  if (!cell.booked) return '';
  return Number.isFinite(cell.percent) ? `${cell.percent}%` : 'Over';
}

function showTip(e, label, sprint, lines) {
  const rect = e.currentTarget.getBoundingClientRect();
  tip.value = { x: rect.left + rect.width / 2, y: rect.top, label, sprint, lines };
}

function phaseTip(e, row, i, cell) {
  showTip(e, row.label, i + 1, cell.length ? [cell.map((p) => p.name).join(', ')] : ['Nothing planned']);
}

function personTip(e, row, cell) {
  const lines = cell.projects.map((x) => `${x.project.name}: ${x.allocation}%`);
  if (cell.away) lines.unshift(cell.booked ? 'Away this sprint but still booked' : 'Away this sprint');
  else lines.push(`Booked ${cell.booked}% of ${cell.capacity}% available`);
  if (!cell.projects.length && !cell.away) lines.splice(0, lines.length, `Free. ${cell.capacity}% available`);
  showTip(e, row.member.name, cell.sprint, lines);
}
</script>

<template>
  <div class="capacity">
    <p v-if="overloaded.length" class="alert">
      {{ overloaded.join(', ') }} {{ overloaded.length === 1 ? 'is' : 'are' }} booked for more time than they have in at least one sprint.
    </p>
    <div class="panels">
      <section class="panel">
        <header class="panel-head">
          <h3>Phase load</h3>
          <p>Projects in each phase, per sprint</p>
        </header>
        <div class="heat" :style="{ '--cols': sprints.length }">
          <span />
          <span v-for="n in sprints" :key="n" class="col-label" :class="{ current: thisSprint === n }" :title="sprintLabel(n)">S{{ n }}</span>
          <template v-for="row in phaseRows" :key="row.key">
            <span class="row-label" :style="row.style"><i class="dot" />{{ row.label }}</span>
            <span
              v-for="(cell, i) in row.cells"
              :key="i"
              class="cell"
              :class="`lvl-${level(cell.length)}`"
              @mouseenter="phaseTip($event, row, i, cell)"
              @mouseleave="tip = null"
            >
              {{ cell.length || '' }}
            </span>
          </template>
        </div>
        <p v-if="!phaseRows.length" class="empty">Add phases in settings to see how work is spread.</p>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h3>People load</h3>
          <p>Share of each person's available time that is booked, per sprint</p>
        </header>
        <div class="heat" :style="{ '--cols': sprints.length }">
          <span />
          <span v-for="n in sprints" :key="n" class="col-label" :class="{ current: thisSprint === n }" :title="sprintLabel(n)">S{{ n }}</span>
          <template v-for="row in people" :key="row.member.id">
            <span class="row-label" :title="row.member.capacity < 100 ? `${row.member.capacity}% available` : 'Full time'">
              {{ row.member.name }}
              <small v-if="row.member.capacity < 100" class="cap">{{ row.member.capacity }}%</small>
            </span>
            <span
              v-for="cell in row.cells"
              :key="cell.sprint"
              class="cell cell--pct"
              :class="[`lvl-${cell.level}`, { over: cell.over, away: cell.away }]"
              @mouseenter="personTip($event, row, cell)"
              @mouseleave="tip = null"
            >
              {{ cellText(cell) }}
            </span>
          </template>
        </div>
        <p v-if="!people.length" class="empty">Add people in settings to see who is stretched.</p>
      </section>
    </div>

    <section class="panel">
      <header class="panel-head">
        <h3>Who is on what</h3>
        <p v-if="unassigned.length">{{ unassigned.length }} planned project{{ unassigned.length === 1 ? ' has' : 's have' }} nobody assigned</p>
        <p v-else>Projects and time booked for each person, per sprint</p>
      </header>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>Person</th>
              <th v-for="n in sprints" :key="n" :class="{ current: thisSprint === n }">{{ sprintLabel(n) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in people" :key="row.member.id">
              <td class="person">{{ row.member.name }}</td>
              <td v-for="cell in row.cells" :key="cell.sprint">
                <span v-if="cell.away" class="chip chip--away">Away</span>
                <span v-for="x in cell.projects" :key="x.project.id" class="chip" :class="{ 'chip--over': cell.over }">{{ x.project.name }} · {{ x.allocation }}%</span>
                <span v-if="!cell.projects.length && !cell.away" class="free">Free</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <Teleport to="body">
      <div v-if="tip" class="tip" :style="{ left: `${tip.x}px`, top: `${tip.y}px` }">
        <strong>{{ tip.label }}, sprint {{ tip.sprint }}</strong>
        <span v-for="line in tip.lines" :key="line">{{ line }}</span>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.capacity {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 48px;
}

.panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.panel {
  min-width: 0;
  padding: 20px 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
}

.panel-head {
  margin-bottom: 16px;

  h3 {
    font-size: 15px;
    font-weight: 600;
  }

  p {
    margin-top: 2px;
    font-size: 12px;
    color: var(--text-3);
  }
}

.heat {
  display: grid;
  grid-template-columns: minmax(80px, 120px) repeat(var(--cols), minmax(0, 1fr));
  gap: 4px;
  align-items: center;
}

.col-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-align: center;
}

.row-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--phase);
  flex-shrink: 0;
}

.cell {
  display: grid;
  place-items: center;
  height: 34px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: default;

  &.lvl-0 {
    background: var(--surface-2);
    box-shadow: inset 0 0 0 1px var(--line);
  }

  &.lvl-1 {
    background: var(--heat-1-bg);
    color: var(--heat-1-text);
  }

  &.lvl-2 {
    background: var(--heat-2-bg);
    color: #fff;
  }

  &.lvl-3 {
    background: var(--heat-3-bg);
    color: #fff;
  }

  &.over {
    box-shadow: inset 0 0 0 2px var(--danger);
  }

  &--pct {
    font-size: 11px;
  }

  &.away {
    background: repeating-linear-gradient(135deg, var(--surface-2) 0 5px, var(--bg) 5px 10px);
    color: var(--text-3);
    box-shadow: inset 0 0 0 1px var(--line);
  }

  &.away.over {
    color: var(--danger);
    box-shadow: inset 0 0 0 2px var(--danger);
  }
}

.col-label.current,
th.current {
  color: var(--today);
  font-weight: 700;
}

.cap {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-3);
}

.alert {
  padding: 10px 14px;
  border-radius: var(--radius-s);
  background: var(--danger-soft);
  color: var(--danger);
  font-size: 13px;
  font-weight: 500;
}

.chip--away {
  background: var(--warn-bg);
  color: var(--warn-text);
}

.chip--over {
  box-shadow: inset 0 0 0 1px var(--danger);
}

.empty {
  font-size: 13px;
  color: var(--text-3);
}

.table-wrap {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 10px;
    border-bottom: 1px solid var(--line);
    text-align: left;
    vertical-align: top;
  }

  th {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-3);
  }

  tr:last-child td {
    border-bottom: 0;
  }
}

.person {
  font-weight: 600;
  white-space: nowrap;
}

.chip {
  display: inline-block;
  margin: 0 4px 4px 0;
  padding: 2px 8px;
  border-radius: 5px;
  background: var(--iris-soft);
  color: var(--iris-strong);
  font-size: 11px;
  font-weight: 600;
}

.free {
  color: var(--text-3);
  font-size: 12px;
}

.tip {
  position: fixed;
  z-index: 950;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 260px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--ink);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  transform: translate(-50%, calc(-100% - 8px));
  pointer-events: none;

  strong {
    color: #fff;
    font-weight: 600;
  }
}
</style>
